import {
  CallDetailsFragment,
  EntryFragment,
} from "@/graphql/generated/graphql";
import { UtteranceType } from "./utterances.utility.decl";

export const isPunctuation = (w: string) => w.startsWith("[");

export const getPunctuation = (w: string) => w.replace(/[[\]']+/g, "");

/**
 * Concatenates punctuation marks to the previous entry in an array of entries.
 *
 * @param entries - The array of entries to process.
 * @returns The processed array of entries.
 */
export const mapEntries = (
  entries: EntryFragment[] | undefined
): EntryFragment[] => {
  if (!entries) {
    return [];
  }

  return entries.reduce((acc: EntryFragment[], currentEntry: EntryFragment) => {
    // Get the previous entry from the end of the acc array.
    const prevEntry = acc[acc.length - 1];

    // If the previous entry exists and the current entry is a punctuation mark,
    // concatenate the punctuation to the previous entry's W property.
    if (prevEntry && isPunctuation(currentEntry.W)) {
      prevEntry.W += getPunctuation(currentEntry.W);
    } else {
      // Otherwise, add the current entry to the result array.
      acc.push({ ...currentEntry });
    }

    return acc;
  }, []);
};

export const mapToUtterances = (
  transcription: CallDetailsFragment["transcription"] | null
): UtteranceType[] | undefined => {
  if (!transcription?.content?.utterances) {
    return;
  }

  if (transcription.__typename === "VoicemailTranscription") {
    return transcription.content.utterances.map((utterance) => ({
      startTime: utterance?.startTime ?? 0,
      endTime: utterance?.endTime ?? 0,
      entries: mapEntries(utterance?.entries),
      channel: 0,
    }));
  }

  return transcription.content.utterances.map((utterance) => ({
    startTime: utterance?.startTime ?? 0,
    endTime: utterance?.endTime ?? 0,
    channel: utterance?.channel ?? 0,
    entries: mapEntries(utterance?.entries),
  }));
};
